// src/widgets/ServicePartnerFinder.tsx
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Autocomplete, GoogleMap, useJsApiLoader } from '@react-google-maps/api'
import { PartnerCard } from './PartnerCard'
import { AdvancedMarker } from "./AdvancedMarker";
import { FilterCheckbox } from "./FilterCheckbox"
import { FiltersState, ServicePartner, Market } from "./types";
import { toNumberOrNull } from "./utils";

type ServiceType = 'retailer' | 'service' | 'partner' | 'econordic'

type LatLng = {
  lat: number
  lng: number
}

interface ServicePartnerFinderProps {
  apiUrl: string
  googleMapsApiKey: string
  page_title?: string
  // Optional: if you have a real Map ID in Google Cloud, pass it in. If not, Advanced Markers may not work reliably.
  mapId?: string
  market?: Market
}

const defaultCenter: LatLng = { lat: 59.91, lng: 10.75 } // Oslo-ish

const ServicePartnerFinder: FC<ServicePartnerFinderProps> = ({
  apiUrl,
  googleMapsApiKey,
  page_title,
  mapId,
  market,
}) => {
  const [partners, setPartners] = useState<ServicePartner[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const [userLocation, setUserLocation] = useState<LatLng | null>(null)
  const [filters, setFilters] = useState<FiltersState>({
    serviceTypes: new Set<ServiceType>(),
  })
  const [filterPanelOpen, setFilterPanelOpen] = useState<boolean>(false)
  const [searchText, setSearchText] = useState<string>('')
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null)
  const [activePartnerId, setActivePartnerId] = useState<number | null>(null)

  const [mapCenter, setMapCenter] = useState<LatLng>(defaultCenter)
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null)

  // Track the latest request to avoid race conditions if user clicks quickly
  const requestSeq = useRef(0)

  const libraries = useMemo(() => ['places', 'marker'] as ('places' | 'marker')[], [])

  const { isLoaded: mapsLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey,
    libraries,
  })

  const [markerLibReady, setMarkerLibReady] = useState(false)

  // Explicitly load marker library (and places) via importLibrary when available.
  // This makes AdvancedMarkerElement availability much more deterministic.
  useEffect(() => {
    if (!mapsLoaded) return

    let cancelled = false

      ; (async () => {
        try {
          // importLibrary exists on newer Maps JS versions
          // @ts-ignore - types may be behind
          if (typeof google.maps.importLibrary === 'function') {
            // @ts-ignore
            await google.maps.importLibrary('marker')
            // @ts-ignore
            await google.maps.importLibrary('places')
          }

          const ok = Boolean(google.maps.marker?.AdvancedMarkerElement)
          if (!cancelled) setMarkerLibReady(ok)
        } catch (e) {
          console.warn('[ServicePartnerFinder] Failed to import marker/places library', e)
          if (!cancelled) setMarkerLibReady(Boolean(google.maps.marker?.AdvancedMarkerElement))
        }
      })()

    return () => {
      cancelled = true
    }
  }, [mapsLoaded])

  // --- Fetch helpers ---------------------------------------------------------

  const buildQuery = (coords: LatLng | null): string => {
    const params = new URLSearchParams()

    if (coords) {
      params.set('lat', String(coords.lat))
      params.set('lng', String(coords.lng))
    }

    if (filters.serviceTypes.size > 0) {
      params.set('service_types', Array.from(filters.serviceTypes).join(','))
    }

    if (market) {
      params.set('market', market)
    }

    const radius = market === 'EXPORT' ? '2000' : '200'
    params.set('radius_km', radius)

    return params.toString()
  }

  const fetchPartners = (coords: LatLng | null): void => {
    if (!apiUrl) {
      setError(gettext('API URL is missing.'))
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const qs = buildQuery(coords)
    const url = qs ? `${apiUrl}?${qs}` : apiUrl

    const seq = ++requestSeq.current

    fetch(url, { credentials: 'same-origin' })
      .then(res => {
        if (!res.ok) throw new Error(`API error ${res.status}`)
        return res.json() as Promise<ServicePartner[]>
      })
      .then(data => {
        // Ignore stale responses
        if (seq !== requestSeq.current) return

        setPartners(data)
        setActivePartnerId(null)

        if (coords) {
          setMapCenter(coords)
        } else if (data.length) {
          const firstLat = toNumberOrNull(data[0].latitude)
          const firstLng = toNumberOrNull(data[0].longitude)
          if (firstLat !== null && firstLng !== null) {
            setMapCenter({ lat: firstLat, lng: firstLng })
          }
        }
      })
      .catch(err => {
        console.error(err)
        if (seq !== requestSeq.current) return
        setError(window.gettext('Could not fetch service partners.'))
      })
      .finally(() => {
        if (seq !== requestSeq.current) return
        setLoading(false)
      })
  }

  // --- On mount: try geolocation ---------------------------------------------

  useEffect(() => {
    if (!navigator.geolocation) {
      fetchPartners(null)
      return
    }

    navigator.geolocation.getCurrentPosition(
      pos => {
        const coords: LatLng = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        }
        setUserLocation(coords)
        fetchPartners(coords)
      },
      () => {
        fetchPartners(null)
      },
      {
        enableHighAccuracy: false,
        timeout: 8000,
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // --- Handlers --------------------------------------------------------------

  // --- Effects ---------------------------------------------------------------

  useEffect(() => {
    fetchPartners(userLocation)
  }, [filters])

  const toggleServiceType = (type: ServiceType): void => {
    setFilters(prev => {
      const next = new Set(prev.serviceTypes)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      return { ...prev, serviceTypes: next }
    })
  }

  const applyFilters = (): void => {
    // fetch is triggered by useEffect on filters change
    setFilterPanelOpen(false)
  }

  const centerMapOnPartner = (partnerId: number) => {
    const p = partners.find(x => x.id === partnerId)
    if (!p) return

    const lat = toNumberOrNull(p.latitude)
    const lng = toNumberOrNull(p.longitude)

    if (lat !== null && lng !== null) {
      setMapCenter({ lat, lng })
    }
  }

  const handlePartnerClick = (partnerId: number) => {
    setActivePartnerId(partnerId)
    centerMapOnPartner(partnerId)
  }

  const handleMarkerClick = (partnerId: number) => {
    setActivePartnerId(partnerId)
    centerMapOnPartner(partnerId)

    const el = document.getElementById(`partner-card-${partnerId}`)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const onPlaceChanged = () => {
    if (!autocomplete) return

    const place = autocomplete.getPlace()

    if (place.geometry && place.geometry.location) {
      const coords = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      }

      setMapCenter(coords)
      setUserLocation(coords)
      fetchPartners(coords)

      if (place.name) setSearchText(place.name)
      return
    }

    console.log('Autocomplete: No details available for input:', place.name)
  }

  // --- Map center fallback if nothing fetched yet ---------------------------

  useEffect(() => {
    if (!userLocation && partners.length) {
      const firstLat = toNumberOrNull(partners[0].latitude)
      const firstLng = toNumberOrNull(partners[0].longitude)
      if (firstLat !== null && firstLng !== null) {
        setMapCenter({ lat: firstLat, lng: firstLng })
      }
    }
  }, [userLocation, partners])

  // --- Render ----------------------------------------------------------------

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
      {/* Left: search + list */}
      <div className="w-full lg:w-[360px] xl:w-[400px] flex flex-col gap-4">
        <h1 className="text-default text-xl font-bold">{page_title}</h1>

        <div className="flex flex-col gap-3">
          <label className="text-default text-label-sm font-semibold">{gettext('Search by location')}</label>

          <div className="relative h-[50px]">
            {mapsLoaded && (
              <Autocomplete
                onLoad={(a) => setAutocomplete(a)}
                onPlaceChanged={onPlaceChanged}
                className="w-full h-full"
              >
                <input
                  type="text"
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  placeholder={window.gettext('Search for a place...')}
                  className="w-full h-full rounded-lg border border-brand-sky-300 bg-transparent pl-4 pr-10 text-body-base text-default placeholder:text-text-subtle outline-none focus:border-brand-sky-500"
                />
              </Autocomplete>
            )}

            <div
              className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center pointer-events-none">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="9" stroke="#39596F" strokeWidth="1.5" />
                <path d="M12 3V6" stroke="#39596F" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M12 18V21" stroke="#39596F" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M21 12H18" stroke="#39596F" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M6 12H3" stroke="#39596F" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
          </div>

          {market !== 'EXPORT' && (
            <button
              type="button"
              onClick={() => setFilterPanelOpen(open => !open)}
              className="flex items-center gap-2 rounded-lg border border-stroke-default px-3 py-1.5 w-fit hover:bg-surface-subtle transition-colors"
            >
              <div className="w-4 h-4 relative flex items-center justify-center">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 4H14" stroke="#39596F" strokeWidth="1.33" strokeLinecap="round" />
                  <path d="M2 8H14" stroke="#39596F" strokeWidth="1.33" strokeLinecap="round" />
                  <path d="M2 12H14" stroke="#39596F" strokeWidth="1.33" strokeLinecap="round" />
                  <circle cx="5" cy="4" r="1.5" fill="white" stroke="#39596F" strokeWidth="1.33" />
                  <circle cx="11" cy="8" r="1.5" fill="white" stroke="#39596F" strokeWidth="1.33" />
                  <circle cx="5" cy="12" r="1.5" fill="white" stroke="#39596F" strokeWidth="1.33" />
                </svg>
              </div>
              <span className="text-default text-label-xs font-medium">{window.gettext('Filter')}</span>
            </button>
          )}
        </div>

        <div className="max-h-[520px] overflow-y-auto space-y-3">
          {loading && (
            <div className="text-body-sm text-text-subtle py-4">
              {window.gettext('Loading service partners...')}
            </div>
          )}

          {error && !loading && (
            <div className="text-body-sm text-error py-4">{error}</div>
          )}

          {!loading && !error && partners.length === 0 && (
            <div className="text-body-sm text-text-subtle py-4">
              {window.gettext('No results for the selected filters.')}
            </div>
          )}

          {!loading && !error && partners.map(partner => (
            <PartnerCard
              key={partner.id}
              partner={partner}
              active={partner.id === activePartnerId}
              onClick={() => handlePartnerClick(partner.id)}
              id={`partner-card-${partner.id}`}
            />
          ))}
        </div>
      </div>

      {/* Filter panel */}
      {filterPanelOpen && (
        <div className="hidden lg:block w-[260px] xl:w-[280px] bg-default rounded-xl shadow-sm p-4 self-start">
          <div className="flex items-center justify-between h-6">
            <h2 className="text-default text-body-base font-bold">{window.gettext('Filter')}</h2>
            <button
              type="button"
              onClick={() => setFilterPanelOpen(false)}
              className="w-6 h-6 flex items-center justify-center"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="#39596F"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-6">
              <h3 className="text-default text-body-base font-medium">{window.gettext('By service type')}</h3>
              <div className="flex flex-col gap-3">
                <FilterCheckbox
                  label={window.gettext('Reseller')}
                  tooltipText={window.gettext('Where can I buy Flexit products?')}
                  checked={filters.serviceTypes.has('retailer')}
                  onChange={() => toggleServiceType('retailer')}
                />
                <FilterCheckbox
                  label={window.gettext('Service company')}
                  tooltipText={window.gettext('Who can help me with service and repairs?')}
                  checked={filters.serviceTypes.has('service')}
                  onChange={() => toggleServiceType('service')}
                />
                <FilterCheckbox
                  label={window.gettext('Flexit Partner')}
                  tooltipText={window.gettext('Who can help me with installation and a service agreement?')}
                  checked={filters.serviceTypes.has('partner')}
                  onChange={() => toggleServiceType('partner')}
                />
                <FilterCheckbox
                  label={window.gettext('EcoNordic Servicepartner')}
                  tooltipText={window.gettext('Who can help me with installation and service of the EcoNordic indoor climate unit?')}
                  checked={filters.serviceTypes.has('econordic')}
                  onChange={() => toggleServiceType('econordic')}
                />
              </div>
            </div>

            <button
              type="button"
              onClick={applyFilters}
              className="mt-6 w-full rounded-full bg-action-primary text-action-primary-foreground text-body-sm py-3 font-semibold hover:bg-opacity-90 transition-opacity"
            >
              {window.gettext('Show results')}
            </button>
          </div>
        </div>
      )}

      {/* Right: Google Map */}
      <div className="flex-1 min-h-[360px] lg:min-h-[540px] bg-default rounded-xl overflow-hidden shadow-sm">
        {!mapsLoaded && !loadError && (
          <div className="w-full h-full flex items-center justify-center text-body-sm text-text-subtle">
            {window.gettext('Loading map...')}
          </div>
        )}

        {loadError && (
          <div className="w-full h-full flex items-center justify-center text-body-sm text-error">
            {window.gettext('Could not load Google Maps.')}
          </div>
        )}

        {mapsLoaded && !loadError && (
          <GoogleMap
            center={mapCenter}
            zoom={8}
            mapContainerStyle={{ width: '100%', height: '100%' }}
            options={{
              disableDefaultUI: false,
              fullscreenControl: false,
              // If you don't have a valid Map ID, either pass one via props or remove advanced marker usage.
              mapId: mapId || 'DEMO_MAP_ID',
            }}
            onLoad={(map) => setMapInstance(map)}
          >
            {/* Only render advanced markers when the marker library is actually available */}
            {mapInstance && markerLibReady && partners.map(p => {
              const lat = toNumberOrNull(p.latitude)
              const lng = toNumberOrNull(p.longitude)
              if (lat === null || lng === null) return null

              return (
                <AdvancedMarker
                  key={p.id}
                  map={mapInstance}
                  position={{ lat, lng }}
                  title={p.name}
                  isActive={p.id === activePartnerId}
                  onClick={() => handleMarkerClick(p.id)}
                />
              )
            })}
          </GoogleMap>
        )}
      </div>
    </div>
  )
}

export default ServicePartnerFinder
